import { baseUrl, httpClient } from 'src/pages/admin/providers/AppDataProvider';
import type {
  LeverageC1StatsResponse,
  LeverageC2StatsResponse,
  LeverageCampaign,
  LeverageCampaignPayload,
  LeveragePayoutsResponse,
  LeveragePoolTotalPayload,
  LeveragePrizePool,
  LeverageStatsQuery,
  LeverageTier,
  LeverageTierPayload,
} from './types';

const resourceBase = 'world-cup/leverage';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { json } = await httpClient(`${baseUrl}/${resourceBase}${path}`, options as any);
  return json as T;
}

const toMs = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const normalizeCampaign = (raw: any): LeverageCampaign => ({
  id: Number(raw?.id ?? 0),
  name: String(raw?.name ?? ''),
  c1StartAt: toMs(raw?.c1StartAt),
  c1EndAt: toMs(raw?.c1EndAt),
  c1IsEnabled: Boolean(raw?.c1IsEnabled),
  c2StartAt: toMs(raw?.c2StartAt),
  c2EndAt: toMs(raw?.c2EndAt),
  c2IsEnabled: Boolean(raw?.c2IsEnabled),
  multiplier: Number(raw?.multiplier ?? 0),
  voucherCap: Number(raw?.voucherCap ?? 0),
});

export const leverageApi = {
  async getCampaign(signal?: AbortSignal): Promise<LeverageCampaign> {
    const data = await request<unknown>('/campaign', { method: 'GET', signal } as any);
    return normalizeCampaign(data);
  },

  async setCampaign(payload: LeverageCampaignPayload): Promise<LeverageCampaign> {
    const toIso = (ms: number | undefined): string | undefined =>
      typeof ms === 'number' && Number.isFinite(ms) ? new Date(ms).toISOString() : undefined;

    const { id: _id, ...rest } = payload as LeverageCampaignPayload & { id?: number };
    const body: Record<string, unknown> = { ...rest };
    if ('c1StartAt' in payload) body.c1StartAt = toIso(payload.c1StartAt);
    if ('c1EndAt' in payload) body.c1EndAt = toIso(payload.c1EndAt);
    if ('c2StartAt' in payload) body.c2StartAt = toIso(payload.c2StartAt);
    if ('c2EndAt' in payload) body.c2EndAt = toIso(payload.c2EndAt);

    const data = await request<unknown>('/campaign', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return normalizeCampaign(data);
  },

  async listTiers(signal?: AbortSignal): Promise<LeverageTier[]> {
    const data = await request<unknown>('/tiers', { method: 'GET', signal } as any);
    return Array.isArray(data) ? (data as LeverageTier[]) : [];
  },

  async upsertTier(payload: LeverageTierPayload): Promise<LeverageTier> {
    return request<LeverageTier>('/tiers', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async deleteTier(tierIndex: number): Promise<void> {
    await request<unknown>(`/tiers/${tierIndex}`, { method: 'DELETE' });
  },

  async getPool(signal?: AbortSignal): Promise<LeveragePrizePool> {
    return request<LeveragePrizePool>('/prize-pool', { method: 'GET', signal } as any);
  },

  async setPoolTotal(payload: LeveragePoolTotalPayload): Promise<LeveragePrizePool> {
    return request<LeveragePrizePool>('/prize-pool/total', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async listC1Stats(
    query: LeverageStatsQuery = {},
    signal?: AbortSignal
  ): Promise<LeverageC1StatsResponse> {
    const qs = buildStatsQuery(query);
    const data = await request<any>(`/c1-stats${qs}`, { method: 'GET', signal } as any);
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number(data?.total ?? 0),
      totalLostAmountUsdc: String(data?.totalLostAmountUsdc ?? '0'),
      totalVoucher: Number(data?.totalVoucher ?? 0),
    };
  },

  async listC2Stats(
    query: LeverageStatsQuery = {},
    signal?: AbortSignal
  ): Promise<LeverageC2StatsResponse> {
    const qs = buildStatsQuery(query);
    const data = await request<any>(`/c2-stats${qs}`, { method: 'GET', signal } as any);
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      totalTeams: Number(data?.totalTeams ?? 0),
      totalTeamVolumeUsdc: String(data?.totalTeamVolumeUsdc ?? '0'),
      totalRewardUsdcReceived: String(data?.totalRewardUsdcReceived ?? '0'),
    };
  },

  async listPayouts(
    query: LeverageStatsQuery = {},
    signal?: AbortSignal
  ): Promise<LeveragePayoutsResponse> {
    const qs = buildStatsQuery(query);
    const data = await request<any>(`/payouts${qs}`, { method: 'GET', signal } as any);
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number(data?.total ?? 0),
    };
  },
};

function buildStatsQuery(query: LeverageStatsQuery): string {
  const params = new URLSearchParams();
  params.set('search', query.search ?? '');
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return `?${params.toString()}`;
}
